import { algoliaSearchClient } from './client';
import { SEARCH, PAGINATION } from '@/constants/app.constants';

export interface AlgoliaSearchParams {
  query: string;
  filters?: Record<string, any>;
  sort_by?: string;
  page?: number;
  per_page?: number;
  facets?: string[];
}

export class AlgoliaSearchService {
  /**
   * Search sequences collection with Algolia
   */
  static async searchSequences(params: AlgoliaSearchParams) {
    if (!algoliaSearchClient) {
      throw new Error('Algolia client not initialized. Check credentials.');
    }

    try {
      // Determine which index to use based on sort option
      let indexName = 'sequences'; // Default primary index

      // Map sort options to replica indices
      if (params.sort_by) {
        if (params.sort_by.includes('created_at:desc')) {
          indexName = 'sequences_created_at_desc';
        } else if (params.sort_by.includes('price:asc')) {
          indexName = 'sequences_price_asc';
        } else if (params.sort_by.includes('price:desc')) {
          indexName = 'sequences_price_desc';
        }
      }

      // Build filter strings for Algolia
      const filterStrings: string[] = [];

      if (params.filters && Object.keys(params.filters).length > 0) {
        // Field mapping from UI to Algolia attributes
        const fieldMapping: Record<string, string> = {
          software: 'software_type',
          genres: 'genres',
          genreSlugs: 'genre_slug',
          artists: 'artist_name',
          holidays: 'holiday_type',
          fileFormats: 'file_type',
          albums: 'album',
          creators: 'creator_name',
        };

        for (const [key, value] of Object.entries(params.filters)) {
          const fieldName = fieldMapping[key] || key;

          // Skip empty values
          if (value === undefined || value === null || value === '' || value === false) {
            continue;
          }

          // Handle price ranges
          if (key === 'minPrice' && typeof value === 'number') {
            filterStrings.push(`price >= ${value}`);
            continue;
          }
          if (key === 'maxPrice' && typeof value === 'number') {
            filterStrings.push(`price <= ${value}`);
            continue;
          }

          // Handle arrays (multi-select filters)
          if (Array.isArray(value)) {
            const nonEmptyValues = value.filter(v => v && typeof v === 'string' && v.trim() !== '');
            if (nonEmptyValues.length > 0) {
              // Create OR filter for array values: (field:value1 OR field:value2)
              const orFilters = nonEmptyValues.map(v => `${fieldName}:"${v}"`).join(' OR ');
              filterStrings.push(`(${orFilters})`);
            }
          } else if (typeof value === 'string' && value.trim() !== '') {
            filterStrings.push(`${fieldName}:"${value}"`);
          } else if (typeof value === 'boolean' && value === true) {
            if (key === 'isFree') {
              filterStrings.push(`is_free:true`);
            } else {
              filterStrings.push(`${fieldName}:true`);
            }
          } else if (typeof value === 'number') {
            filterStrings.push(`${fieldName}:${value}`);
          }
        }
      }

      // Prepare search parameters - Algolia v5 uses client.search() with array format
      const facetsList = params.facets || [
        'software_type',
        'holiday_type',
        'genres',
        'creator_name',
        'is_free',
        'album',
        'artist_name',
        'props_used_names',
        'props_category_slugs',
        'has_moving_heads',
      ];

      // Build query parameters object
      const queryParams: any = {
        page: (params.page || 1) - 1, // Algolia uses 0-indexed pages
        hitsPerPage: params.per_page || PAGINATION.SEARCH_PAGE_SIZE,
        facets: facetsList,
        maxValuesPerFacet: 200, // Match Typesense max_facet_values
      };

      // Add filters if any
      if (filterStrings.length > 0) {
        queryParams.filters = filterStrings.join(' AND ');
      }

      // Log the search request for debugging
      console.log('[Algolia Debug] Search request:', {
        indexName,
        query: params.query || '*',
        filters: filterStrings.length > 0 ? filterStrings.join(' AND ') : 'none',
        facets: facetsList,
        page: queryParams.page,
        hitsPerPage: queryParams.hitsPerPage,
      });

      // Execute search using Algolia v5 client.search() API with array of queries
      const searchResponse = await algoliaSearchClient.search([
        {
          indexName,
          query: params.query || '*',
          params: queryParams,
        },
      ]);

      // Log the response for debugging
      if (searchResponse?.results?.[0]) {
        const result = searchResponse.results[0];
        console.log('[Algolia Debug] Search response:', {
          nbHits: result.nbHits,
          hitsCount: result.hits?.length || 0,
          facetsCount: Object.keys(result.facets || {}).length,
          page: result.page,
          processingTimeMS: result.processingTimeMS,
        });

        // Log facet details
        if (result.facets) {
          console.log('[Algolia Debug] Available facets:', Object.keys(result.facets));
          if (result.facets.holiday_type) {
            console.log('[Algolia Debug] Holiday facet values:', Object.keys(result.facets.holiday_type));
          }
        }
      }

      // Extract result from array (client.search returns results array)
      const result = searchResponse.results[0];

      // Validate response structure
      if (!result) {
        throw new Error('Algolia returned empty results array');
      }

      // Transform response to match Typesense structure
      return {
        hits: (result.hits || []).map((hit: any) => ({
          document: hit,
          highlights: hit._highlightResult || {},
        })),
        found: result.nbHits || 0,
        search_time_ms: result.processingTimeMS || 0,
        facet_counts: this.transformFacets(result.facets || {}),
        page: (result.page || 0) + 1, // Convert back to 1-indexed
        out_of: Math.ceil((result.nbHits || 0) / (params.per_page || PAGINATION.SEARCH_PAGE_SIZE)),
      };
    } catch (error: any) {
      console.error('[Algolia Error] Search failed:', {
        message: error.message,
        status: error.status,
        errorCode: error.errorCode,
        fullError: error,
      });
      throw new Error(`Algolia sequences search failed: ${error.message}`);
    }
  }

  /**
   * Transform Algolia facets to match Typesense structure
   */
  private static transformFacets(algoliaFacets: Record<string, Record<string, number>>): any[] {
    const facetCounts: any[] = [];

    for (const [fieldName, facetValues] of Object.entries(algoliaFacets)) {
      const counts = Object.entries(facetValues).map(([value, count]) => ({
        value,
        count,
        highlighted: value, // No highlighting in facets for now
      }));

      facetCounts.push({
        field_name: fieldName,
        counts,
        stats: {}, // Algolia doesn't provide stats by default
      });
    }

    return facetCounts;
  }

  /**
   * Health check for Algolia
   */
  static async healthCheck() {
    if (!algoliaSearchClient) {
      return {
        status: 'unhealthy',
        error: 'Algolia client not initialized',
        message: 'Algolia credentials missing',
      };
    }

    try {
      const result = await this.searchSequences({
        query: 'christmas',
        per_page: 1,
      });

      return {
        status: 'healthy',
        sequencesCount: result.found || 0,
        message: 'Algolia search is operational',
      };
    } catch (error: any) {
      console.error('Algolia health check failed:', error);
      return {
        status: 'unhealthy',
        error: error.message,
        message: 'Algolia search is not responding',
      };
    }
  }
}
